import { AlertTriangle, PieChart, X, PiggyBank } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import { Button } from '../ui/button';
import type { ComponentProps } from 'react';

export type AlertSeverity = 'income' | 'budget' | 'error';

export interface BudgetAlertProps extends Omit<
  ComponentProps<'div'>,
  'children'
> {
  severity: AlertSeverity;
  title: string;
  message: string;
  onDismiss?: () => void;
}

const severityConfig = {
  income: {
    icon: PiggyBank,
    className: 'border-blue-500 bg-blue-50 text-blue-900',
    iconClassName: 'text-blue-600',
  },
  budget: {
    icon: PieChart,
    className: 'border-blue-500 bg-blue-50 text-blue-900',
    iconClassName: 'text-blue-600',
  },
  error: {
    icon: AlertTriangle,
    className: 'border-red-500 bg-red-50 text-red-900',
    iconClassName: 'text-red-600',
  },
};

export function BudgetAlert({
  severity,
  title,
  message,
  onDismiss,
  className,
  ...props
}: BudgetAlertProps) {
  const config = severityConfig[severity];
  const Icon = config.icon;

  return (
    <div
      data-slot="budget-alert"
      className={twMerge(
        'flex items-start gap-3 rounded-lg border-l-4 p-4',
        config.className,
        className
      )}
      role="alert"
      {...props}
    >
      <Icon className={twMerge('size-5 shrink-0', config.iconClassName)} />

      <div className="flex flex-1 flex-col gap-1">
        <h4 className="text-sm font-semibold">{title}</h4>
        <p className="text-sm opacity-90">{message}</p>
      </div>

      {onDismiss && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onDismiss}
          aria-label="Fechar alerta"
          className="h-6 px-1"
        >
          <X className="size-4" />
        </Button>
      )}
    </div>
  );
}

export interface Alert {
  id: string;
  severity: AlertSeverity;
  title: string;
  message: string;
  onClick: () => void;
}

export interface BudgetAlertsContainerProps extends Omit<
  ComponentProps<'div'>,
  'children'
> {
  alerts: Alert[];
  onDismiss?: (id: string) => void;
}

export function BudgetAlertsContainer({
  alerts,
  onDismiss,
  className,
  ...props
}: BudgetAlertsContainerProps) {
  if (alerts.length === 0) return null;

  return (
    <div
      data-slot="budget-alerts-container"
      className={twMerge('flex flex-col gap-2', className)}
      {...props}
    >
      {alerts.map((alert) => (
        <BudgetAlert
          key={alert.id}
          severity={alert.severity}
          title={alert.title}
          message={alert.message}
          onClick={alert.onClick}
          onDismiss={onDismiss ? () => onDismiss(alert.id) : undefined}
          className="cursor-pointer"
        />
      ))}
    </div>
  );
}
